import { AbsoluteFill, interpolate, useCurrentFrame } from "remotion";
import {
  Caption,
  Callout,
  COLORS,
  FONTS,
  MetricCounter,
  Panel,
  PipelineGraph,
  SceneTransition,
  SlideTimeBar,
  type PipeNode,
} from "../../coldcompute";
import { SCENE } from "./constants";
import {
  HOOK,
  ORIGIN,
  STACK,
  BLOCK,
  SAVED,
  RIGHT,
  WRONG,
  MONEY,
  OUTRO,
} from "./script";

const center: React.CSSProperties = {
  alignItems: "center",
  justifyContent: "center",
  flexDirection: "column",
};

const Kicker: React.FC<{ text: string; color: string }> = ({ text, color }) => (
  <div
    style={{
      fontFamily: FONTS.mono,
      fontSize: 22,
      letterSpacing: "0.22em",
      textTransform: "uppercase",
      color,
      marginBottom: 22,
    }}
  >
    ▍{text}
  </div>
);

const Big: React.FC<{ children: React.ReactNode; size?: number }> = ({
  children,
  size = 92,
}) => (
  <div
    style={{
      fontFamily: FONTS.display,
      fontWeight: 700,
      fontSize: size,
      lineHeight: 0.98,
      letterSpacing: "-0.02em",
      color: COLORS.text,
      textAlign: "center",
      maxWidth: 1560,
    }}
  >
    {children}
  </div>
);

/* 01 — HOOK */
export const Scene01Hook: React.FC = () => {
  const frame = useCurrentFrame();
  const cut = interpolate(frame, [38, 64], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.hook.duration} />
      <SceneTransition from="up">
        <AbsoluteFill style={center}>
          <Kicker text="honest solo journey · ep 08" color={COLORS.cyan} />
          <Big>
            my government
            <br />
            <span style={{ color: COLORS.red, opacity: 0.4 + cut * 0.6 }}>
              BLOCKED MY DATABASE
            </span>
          </Big>
        </AbsoluteFill>
      </SceneTransition>
      <Caption lines={HOOK} accent="cyan" />
    </AbsoluteFill>
  );
};

/* 02 — ORIGIN (timeline) */
const TIMELINE: PipeNode[] = [
  { label: "before", value: "MICROSOFT", sub: "engineer", accent: "violet" },
  { label: "decision", value: "WENT SOLO", sub: "no co-founder", accent: "cyan" },
  { label: "built", value: "EAKC", sub: "~2 months", accent: "green" },
];
export const Scene02Origin: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.origin.duration} />
    <AbsoluteFill style={{ ...center, padding: "0 110px" }}>
      <Kicker text="from microsoft to solo" color={COLORS.cyan} />
      <div style={{ width: 1500 }}>
        <PipelineGraph nodes={TIMELINE} startFrame={8} stepFrames={26} nodeWidth={300} />
      </div>
      <div style={{ fontFamily: FONTS.mono, fontSize: 24, color: COLORS.dim, marginTop: 40 }}>
        late dec 2025 → early march 2026 · one person, no funding
      </div>
    </AbsoluteFill>
    <Caption lines={ORIGIN} accent="cyan" />
  </AbsoluteFill>
);

/* 03 — STACK (boring tech + cost) */
const STACK_ROWS: [string, string, "cyan" | "green" | "lime" | "violet" | "amber"][] = [
  ["Next.js", "one framework, one deploy", "cyan"],
  ["Supabase · Postgres + pgvector", "SQL I can take anywhere", "green"],
  ["OpenRouter", "swap models via one env var", "violet"],
  ["Railway · Hostinger · Upstash", "cheap, multi-region", "lime"],
];
export const Scene03Stack: React.FC = () => {
  const frame = useCurrentFrame();
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.stack.duration} />
      <AbsoluteFill style={{ ...center, padding: "0 90px" }}>
        <Kicker text="boring tech, on purpose" color={COLORS.green} />
        <div style={{ display: "flex", gap: 40, alignItems: "center" }}>
          <Panel accent="green" title="the stack" width={840} padding={20}>
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              {STACK_ROWS.map(([name, why, ac], i) => {
                const o = interpolate(frame, [10 + i * 12, 30 + i * 12], [0, 1], {
                  extrapolateLeft: "clamp",
                  extrapolateRight: "clamp",
                });
                return (
                  <div
                    key={name}
                    style={{
                      opacity: o,
                      transform: `translateX(${(1 - o) * -14}px)`,
                      display: "flex",
                      flexDirection: "column",
                      borderLeft: `3px solid ${COLORS[ac]}`,
                      paddingLeft: 16,
                    }}
                  >
                    <div style={{ fontFamily: FONTS.mono, fontSize: 26, color: COLORS.text }}>
                      {name}
                    </div>
                    <div style={{ fontFamily: FONTS.body, fontSize: 20, color: COLORS.dim }}>
                      {why}
                    </div>
                  </div>
                );
              })}
            </div>
          </Panel>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
            <div style={{ fontFamily: FONTS.mono, fontSize: 20, letterSpacing: "0.18em", color: COLORS.amber, textTransform: "uppercase" }}>
              total infra / month
            </div>
            <div style={{ display: "flex", alignItems: "baseline" }}>
              <span style={{ fontFamily: FONTS.display, fontWeight: 700, fontSize: 90, color: COLORS.amber }}>~$</span>
              <MetricCounter value={25} from={0} startFrame={12} durationFrames={48} accent="amber" fontSize={150} />
            </div>
            <div style={{ fontFamily: FONTS.body, fontSize: 22, color: COLORS.dim, marginTop: 6 }}>
              free tiers + pay-per-use
            </div>
          </div>
        </div>
      </AbsoluteFill>
      <Caption lines={STACK} accent="green" />
    </AbsoluteFill>
  );
};

/* 04 — THE BLOCK */
export const Scene04Block: React.FC = () => {
  const frame = useCurrentFrame();
  const flash = 0.5 + 0.5 * Math.sin(frame / 6);
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.block.duration} />
      <SceneTransition from="up">
        <AbsoluteFill style={center}>
          <Kicker text="0:34 · the supabase india block" color={COLORS.red} />
          <Panel accent="red" title="dashboard · supabase.com" width={900} padding={0}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: 240, gap: 14 }}>
              <div style={{ fontFamily: FONTS.mono, fontSize: 30, color: COLORS.red, opacity: 0.5 + flash * 0.5 }}>
                ⨯ ERR_CONNECTION_TIMED_OUT
              </div>
              <div style={{ fontFamily: FONTS.mono, fontSize: 22, color: COLORS.dim }}>
                blocked at the ISP level · status page: green
              </div>
            </div>
          </Panel>
          <div style={{ fontFamily: FONTS.body, fontSize: 26, color: COLORS.dim, marginTop: 26 }}>
            different browser · new router · still timeout — it wasn't me
          </div>
        </AbsoluteFill>
      </SceneTransition>
      <Caption lines={BLOCK} accent="red" />
    </AbsoluteFill>
  );
};

/* 05 — SAVED (geographic diversity) */
const SAVED_PIPE: PipeNode[] = [
  { label: "dev machine", value: "INDIA", sub: "✗ blocked", accent: "red" },
  { label: "via", value: "VPN", sub: "us / eu exit", accent: "amber" },
  { label: "prod", value: "RAILWAY · HOSTINGER", sub: "✓ us / eu", accent: "green" },
  { label: "db", value: "SUPABASE", sub: "✓ reachable", accent: "cyan" },
];
export const Scene05Saved: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.saved.duration} />
    <AbsoluteFill style={{ ...center, padding: "0 80px" }}>
      <Kicker text="what saved production" color={COLORS.green} />
      <div style={{ width: 1660 }}>
        <PipelineGraph nodes={SAVED_PIPE} startFrame={6} stepFrames={22} nodeWidth={230} />
      </div>
      <div style={{ fontFamily: FONTS.body, fontSize: 26, color: COLORS.dim, marginTop: 40, textAlign: "center", maxWidth: 1300 }}>
        the block hit residential ISPs — not US/EU cloud. multi-region infra meant
        prod never went down.
      </div>
    </AbsoluteFill>
    <Caption lines={SAVED} accent="green" />
  </AbsoluteFill>
);

/* 06 — WHAT WENT RIGHT */
export const Scene06Right: React.FC = () => {
  const frame = useCurrentFrame();
  const rows = [
    "tenant_id on every table — multi-tenant from mig 001, RLS from mig 002",
    "RAG built incrementally: vector → hybrid → rerank → cache → graph",
    "MCP server: 3 tools (query_eakc · list_sources · get_document)",
    "real production deployment — live, serving real queries",
  ];
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.right.duration} />
      <AbsoluteFill style={center}>
        <Kicker text="what went right" color={COLORS.green} />
        <div style={{ display: "flex", flexDirection: "column", gap: 18, width: 1280 }}>
          {rows.map((r, i) => {
            const o = interpolate(frame, [14 + i * 18, 36 + i * 18], [0, 1], {
              extrapolateLeft: "clamp",
              extrapolateRight: "clamp",
            });
            return (
              <div
                key={i}
                style={{
                  opacity: o,
                  transform: `translateX(${(1 - o) * -18}px)`,
                  display: "flex",
                  alignItems: "center",
                  gap: 18,
                  background: COLORS.panel,
                  border: `1px solid ${COLORS.green}44`,
                  borderRadius: 10,
                  padding: "16px 22px",
                }}
              >
                <span style={{ fontFamily: FONTS.display, fontWeight: 700, fontSize: 30, color: COLORS.green }}>✓</span>
                <span style={{ fontFamily: FONTS.mono, fontSize: 25, color: COLORS.text }}>{r}</span>
              </div>
            );
          })}
        </div>
      </AbsoluteFill>
      <Caption lines={RIGHT} accent="green" />
    </AbsoluteFill>
  );
};

/* 07 — WHAT WENT WRONG */
export const Scene07Wrong: React.FC = () => {
  const frame = useCurrentFrame();
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.wrong.duration} />
      <AbsoluteFill style={center}>
        <Kicker text="what went wrong (honest)" color={COLORS.red} />
        <div style={{ display: "flex", gap: 30, alignItems: "stretch" }}>
          <Panel accent="red" title="sync hung at 22%" width={560}>
            <div style={{ fontFamily: FONTS.body, fontSize: 24, lineHeight: 1.5, color: COLORS.text }}>
              Ran the worker inside Next.js via <span style={{ fontFamily: FONTS.mono, color: COLORS.amber }}>setTimeout</span>.
              The framework&apos;s patched <span style={{ fontFamily: FONTS.mono, color: COLORS.amber }}>fetch</span> deadlocked
              the event loop.
            </div>
            <div style={{ fontFamily: FONTS.mono, fontSize: 20, color: COLORS.red, marginTop: 14 }}>
              3 days to diagnose · 5 wrong hypotheses
            </div>
          </Panel>
          <Panel accent="amber" title="openclaw integration" width={560}>
            <div style={{ fontFamily: FONTS.body, fontSize: 24, lineHeight: 1.5, color: COLORS.text }}>
              A full week building an MCP skill the platform simply ignored —
              it generated its own keys.
            </div>
            <div style={{ fontFamily: FONTS.mono, fontSize: 20, color: COLORS.amber, marginTop: 14 }}>
              should&apos;ve tested limits first
            </div>
          </Panel>
        </div>
        <div
          style={{
            opacity: interpolate(frame, [60, 90], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" }),
            fontFamily: FONTS.body,
            fontSize: 24,
            color: COLORS.dim,
            marginTop: 26,
          }}
        >
          no monitoring on day one · tested with 10 files, prod had 625 · scope crept
        </div>
      </AbsoluteFill>
      <Caption lines={WRONG} accent="red" />
    </AbsoluteFill>
  );
};

/* 08 — REALISTIC FINANCIALS */
export const Scene08Money: React.FC = () => {
  const frame = useCurrentFrame();
  const c1 = interpolate(frame, [120, 150], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.money.duration} />
      <AbsoluteFill style={center}>
        <Kicker text="realistic financials" color={COLORS.amber} />
        <div style={{ display: "flex", alignItems: "baseline", gap: 14 }}>
          <span style={{ fontFamily: FONTS.display, fontWeight: 700, fontSize: 110, color: COLORS.amber }}>$</span>
          <MetricCounter value={0} from={0} startFrame={6} durationFrames={20} accent="amber" fontSize={210} />
        </div>
        <div style={{ fontFamily: FONTS.body, fontSize: 30, color: COLORS.dim, marginTop: 6 }}>
          revenue · pre-launch · no paying customers yet
        </div>
        <div style={{ display: "flex", gap: 26, marginTop: 40 }}>
          <Callout label="cost" accent="green" width={300}>
            <b style={{ color: COLORS.text }}>~$25/mo</b> · indefinitely sustainable
          </Callout>
          <div style={{ opacity: c1 }}>
            <Callout label="the real value" accent="cyan" width={360}>
              technical depth that <b style={{ color: COLORS.text }}>doesn&apos;t depreciate</b>
            </Callout>
          </div>
        </div>
      </AbsoluteFill>
      <Caption lines={MONEY} accent="amber" />
    </AbsoluteFill>
  );
};

/* 09 — OUTRO */
export const Scene09Outro: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.outro.duration} />
    <SceneTransition from="up">
      <AbsoluteFill style={center}>
        <Big size={70}>
          The code is real.
          <br />
          The bugs were real.
          <br />
          <span style={{ color: COLORS.cyan }}>Building solo is possible.</span>
        </Big>
        <div style={{ fontFamily: FONTS.body, fontSize: 28, color: COLORS.dim, marginTop: 30 }}>
          Not easy. Not glamorous. Possible.
        </div>
        <div style={{ fontFamily: FONTS.mono, fontSize: 24, letterSpacing: "0.22em", color: COLORS.dim, marginTop: 44 }}>
          EAKC · built in public
        </div>
      </AbsoluteFill>
    </SceneTransition>
    <Caption lines={OUTRO} accent="cyan" />
  </AbsoluteFill>
);

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
